'use client'

import { useState } from 'react'
import Image from 'next/image'
import type { Color, Product } from '@/lib/types'
import ColorImageModal from './ColorImageModal'

interface ColorSelectorProps {
  colors: Color[]
  product: Product
  selectedColorId?: string
  onColorSelect?: (colorId: string) => void
}

export default function ColorSelector({
  colors,
  product,
  selectedColorId: externalSelectedColorId,
  onColorSelect,
}: ColorSelectorProps) {
  const [internalSelectedColorId, setInternalSelectedColorId] = useState<string>(
    colors[0]?.id || ''
  )
  const [modalOpen, setModalOpen] = useState(false)
  const [modalImageUrl, setModalImageUrl] = useState('')
  const [modalColorName, setModalColorName] = useState('')

  const selectedColorId = externalSelectedColorId ?? internalSelectedColorId

  if (colors.length === 0) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        <p className="text-gray-600 text-sm">No colors available at this time.</p>
      </div>
    )
  }

  const handleColorClick = (color: Color) => {
    if (onColorSelect) {
      onColorSelect(color.id)
    } else {
      setInternalSelectedColorId(color.id)
    }
  }

  const handleImageClick = (e: React.MouseEvent, color: Color) => {
    if (color.image_path) {
      e.stopPropagation()
      const imageUrl = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/color-images/${color.image_path}`
      setModalImageUrl(imageUrl)
      setModalColorName(color.name)
      setModalOpen(true)
    }
  }

  return (
    <>
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Pick a Color<span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {colors.map((color) => {
            const isSelected = selectedColorId === color.id
            const imageUrl = color.image_path
              ? `${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/color-images/${color.image_path}`
              : null

            return (
              <button
                key={color.id}
                type="button"
                onClick={() => handleColorClick(color)}
                className={`p-3 rounded-2xl border-2 transition-all shadow-soft ${
                  isSelected
                    ? 'border-primary bg-primary/10'
                    : 'border-gray-200 hover:border-secondary'
                }`}
              >
                <div className="flex items-center space-x-2">
                  {imageUrl ? (
                    <div
                      className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 cursor-zoom-in relative group"
                      onClick={(e) => handleImageClick(e, color)}
                    >
                      <Image
                        src={imageUrl}
                        alt={color.name}
                        width={36}
                        height={36}
                        className="object-cover w-full h-full transition-transform group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-full" />
                    </div>
                  ) : color.hex_code ? (
                    <div
                      className="w-9 h-9 rounded-full border border-gray-300 flex-shrink-0"
                      style={{ backgroundColor: color.hex_code }}
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-gray-200 flex-shrink-0" />
                  )}
                  <span className="text-sm font-medium text-left flex-1">{color.name}</span>
                </div>
              </button>
            )
          })}
        </div>
        {colors.some((c) => c.image_path) && (
          <p className="text-xs text-gray-500 italic">
            Click on color sample images to view larger
          </p>
        )}
      </div>
      <ColorImageModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        imageUrl={modalImageUrl}
        colorName={modalColorName}
      />
    </>
  )
}
